(function () {
    const NATIVE_AIS_SELECTOR = "#native-ais";
    const SEARCH_PAGE_PATH_REGEX = /(^|\/)search(\/|$)/;
    const COLLECTION_PAGE_PATH_REGEX = /(^|\/)collections\/([^\/?#]+)/;
    const TECH_SPEC_ROOT_ATTRIBUTE = "meta.algolia.flattenTechSpecs";
    const HIERARCHY_SEPARATOR = " > ";
    const DEFAULT_DYNAMIC_FACET_PRIORITY = 999999;
    const NATIVE_WAIT_INTERVAL_MS = 100;
    const NATIVE_WAIT_MAX_TRIES = 80;
    const HITS_PER_PAGE = 12;
    const HIERARCHICAL_MENU_LIMIT = 30;
    const MAX_VISIBLE_PAGINATION_PAGES = 5;
    const DYNAMIC_FACETS_SELECTOR = "#native-dynamic-facets";
    const PAGINATION_SELECTOR = "#native-pagination";
    const HIERARCHICAL_MENU_SELECTOR = "#native-hierarchical-menu";
    const SECONDARY_FACETS_SELECTOR = "#native-secondary-facets";
    const SECONDARY_FACETS_HEADER_SELECTOR = ".native-secondary-facets-header";
    const CLEAR_REFINEMENTS_SELECTOR = "#native-clear-refinements";
    const CLEAR_REFINEMENTS_BUTTON_SELECTOR =
        "#native-clear-refinements .ais-ClearRefinements-button";
    const HITS_SELECTOR = "#native-hits";
    const STATS_SELECTOR = "#native-stats";
    const SORT_WIDGET_SELECTOR = "#native-sort-widget";
    const FACETS_TOGGLE_SELECTOR = "#native-facets-toggle";
    const LOCALE_PATH_SEGMENT_REGEX = /^[a-z]{2}-[a-z]{2}$/i;
    const INITIAL_NATIVE_SEARCH_STATE = Object.freeze({
        search: null,
        searchClient: null,
        indexName: "",
        hierarchyItems: [],
        dynamicFacetAttributes: [],
    });

    const nativeSearchState = {
        ...INITIAL_NATIVE_SEARCH_STATE,
    };

    const nativeSearchDomCache = new Map();
    const dynamicFacetValueOrderByAttribute = new Map();
    let browserTitleSuffix = null;

    function waitForNativeAlgolia() {
        return new Promise((resolve, reject) => {
            let tries = 0;

            const timer = setInterval(() => {
                tries++;

                const algoliaLiteReady =
                    typeof window["algoliasearch/lite"]?.liteClient === "function" ||
                    typeof window.algoliasearch === "function";

                const ready =
                    algoliaLiteReady &&
                    typeof window.instantsearch === "function" &&
                    window.algoliaShopify?.config?.app_id &&
                    window.algoliaShopify?.config?.search_api_key &&
                    document.querySelector(NATIVE_AIS_SELECTOR);

                if (ready) {
                    clearInterval(timer);
                    resolve();
                }

                if (tries >= NATIVE_WAIT_MAX_TRIES) {
                    clearInterval(timer);
                    reject(new Error("Native InstantSearch dependencies not ready"));
                }
            }, NATIVE_WAIT_INTERVAL_MS);
        });
    }

    const TECH_SPEC_PREFIX = "meta.algolia.flattenTechSpecs.";
    const CATEGORY_ATTRIBUTE = "meta.custom.category_hierarchy.lvl0";
    const YMM_ATTRIBUTE = "meta.custom.year_make_model_full";
    const HIERARCHICAL_ATTRIBUTES = [
        CATEGORY_ATTRIBUTE,
        "meta.custom.category_hierarchy.lvl1",
        "meta.custom.category_hierarchy.lvl2",
        "meta.custom.category_hierarchy.lvl3",
    ];

    function getAlgoliaConfig() {
        return window.algoliaShopify?.config || {};
    }

    function getCurrentMarketIndexSuffix() {
        const algoliaConfig = getAlgoliaConfig();
        const marketMappings = Array.isArray(algoliaConfig.markets_mapping)
            ? algoliaConfig.markets_mapping
            : [];
        const countryCode = String(
            window.shopifyLocalization?.countryCode ||
            window.language?.marketCode ||
            ""
        ).toUpperCase();
        const languageCode = String(
            window.shopifyLocalization?.languageCode ||
            window.language?.code ||
            ""
        ).toLowerCase();

        const marketMapping = marketMappings.find((mapping) => {
            const regions = [
                ...(Array.isArray(mapping?.regions) ? mapping.regions : []),
                ...(Array.isArray(mapping?.all_regions) ? mapping.all_regions : []),
            ].map((region) => String(region).toUpperCase());

            return (
                regions.includes(countryCode) &&
                String(mapping?.locale || "").toLowerCase() === languageCode
            );
        });

        return String(marketMapping?.suffix || "").trim();
    }

    function resetNativeSearchState() {
        nativeSearchState.search = null;
        nativeSearchState.searchClient = null;
        nativeSearchState.indexName = "";
        nativeSearchState.hierarchyItems = [];
        nativeSearchState.dynamicFacetAttributes = [];
    }

    function getNativeSearchState() {
        return nativeSearchState;
    }

    function setNativeSearchState(updates = {}) {
        Object.assign(nativeSearchState, updates);
        return nativeSearchState;
    }

    function getNativeSearchTranslations() {
        return window.__nativeSearchTranslations || {};
    }

    function translateNativeSearch(key, fallback) {
        const translations = getNativeSearchTranslations();
        const value = translations[key];

        return typeof value === "string" && value.trim() ? value : fallback;
    }

    function interpolateTranslation(template, replacements = {}) {
        return Object.keys(replacements).reduce((output, token) => {
            const value = replacements[token];
            return output.split(token).join(String(value));
        }, template);
    }

    function getClearRefinementsLabel() {
        return translateNativeSearch("clearAll", "Clear all");
    }

    function getResultsFoundLabel(count) {
        const template = translateNativeSearch(
            "resultsWithCount",
            "__COUNT__ results found"
        );

        return interpolateTranslation(template, {
            "__COUNT__": count.toLocaleString(),
        });
    }

    function getSearchBrowserTitle(count, terms) {
        const template = translateNativeSearch(
            "resultsWithCountAndTerm",
            "__COUNT__ results found for “__TERMS__”"
        );

        return interpolateTranslation(template, {
            "__COUNT__": count.toLocaleString(),
            "__TERMS__": terms,
        });
    }

    function getBrowserTitleSuffix() {
        if (browserTitleSuffix !== null) {
            return browserTitleSuffix;
        }

        const title = document.title;
        const separators = [" – ", " - ", " — "];

        for (const separator of separators) {
            const parts = title.split(separator);
            if (parts.length > 1) {
                browserTitleSuffix = separator + parts[parts.length - 1];
                return browserTitleSuffix;
            }
        }

        browserTitleSuffix = "";
        return browserTitleSuffix;
    }

    function updateBrowserTitle(count) {
        const terms = getSearchQuery().trim();
        const countValue = Number(count);

        if (!terms || !Number.isFinite(countValue)) {
            return;
        }

        const title = getSearchBrowserTitle(countValue, terms);
        const suffix = getBrowserTitleSuffix();

        document.title = `${title}${suffix}`;
    }

    function getPaginationAriaLabel(pageNumber) {
        const template = translateNativeSearch("pageLabel", "Page __PAGE__");

        return interpolateTranslation(template, {
            "__PAGE__": pageNumber,
        });
    }

    function getSortByRelevanceLabel() {
        return translateNativeSearch("sortByRelevance", "Relevance");
    }

    function getSortByMostPopularLabel() {
        return translateNativeSearch("sortByMostPopular", "Most popular");
    }

    function getItemNumberLabel() {
        return translateNativeSearch("itemNumberLabel", "Item");
    }

    function getMostPopularIndexName(indexName) {
        return `${indexName}_recently_ordered_count_desc`;
    }

    function queryElement(selector) {
        if (
            selector === NATIVE_AIS_SELECTOR ||
            selector === SECONDARY_FACETS_SELECTOR ||
            selector === SECONDARY_FACETS_HEADER_SELECTOR ||
            selector === HIERARCHICAL_MENU_SELECTOR ||
            selector === DYNAMIC_FACETS_SELECTOR ||
            selector === PAGINATION_SELECTOR ||
            selector === CLEAR_REFINEMENTS_SELECTOR ||
            selector === HITS_SELECTOR ||
            selector === STATS_SELECTOR
        ) {
            if (!nativeSearchDomCache.has(selector)) {
                nativeSearchDomCache.set(selector, document.querySelector(selector));
            }

            return nativeSearchDomCache.get(selector);
        }

        return document.querySelector(selector);
    }

    function setInitializingState(isInitializing) {
        queryElement(NATIVE_AIS_SELECTOR)?.classList.toggle(
            "is-initializing",
            Boolean(isInitializing)
        );
    }

    function toggleLeafCollectionState(isLeafCollection) {
        queryElement(NATIVE_AIS_SELECTOR)?.classList.toggle(
            "is-leaf-collection",
            Boolean(isLeafCollection)
        );
    }

    function toggleSecondaryFacetsVisibility(isVisible) {
        queryElement(SECONDARY_FACETS_SELECTOR)?.classList.toggle(
            "is-visible",
            Boolean(isVisible)
        );
    }

    function getConfiguredProductsIndexName() {
        const algoliaConfig = getAlgoliaConfig();
        const baseIndexName = (
            algoliaConfig.index_name ||
            algoliaConfig.products_index_name ||
            (algoliaConfig.index_prefix
                ? `${algoliaConfig.index_prefix}products`
                : "")
        );
        const marketIndexSuffix = getCurrentMarketIndexSuffix();

        if (!baseIndexName || !marketIndexSuffix) {
            return baseIndexName;
        }

        const normalizedSuffix = `_${marketIndexSuffix}`;

        return baseIndexName.endsWith(normalizedSuffix)
            ? baseIndexName
            : `${baseIndexName}${normalizedSuffix}`;
    }

    function getIndexName() {
        const configuredIndexName = getConfiguredProductsIndexName();

        if (configuredIndexName) {
            return configuredIndexName;
        }

        return "lawson_staging_products";
    }

    function getCurrentLocalePathPrefix() {
        const rootUrl = String(window.language?.root_url || "").trim();

        if (rootUrl && rootUrl !== "/") {
            return rootUrl.startsWith("/") ? rootUrl : `/${rootUrl}`;
        }

        const firstSegment = window.location.pathname
            .split("/")
            .filter(Boolean)[0];

        if (LOCALE_PATH_SEGMENT_REGEX.test(firstSegment || "")) {
            return `/${firstSegment}`;
        }

        return "";
    }

    function localizeStorefrontUrl(url) {
        if (!url || url === "#") return url;

        try {
            const parsedUrl = new URL(url, window.location.origin);

            if (parsedUrl.origin !== window.location.origin) {
                return url;
            }

            const localePrefix = getCurrentLocalePathPrefix();
            let pathname = parsedUrl.pathname;

            if (
                localePrefix &&
                pathname !== localePrefix &&
                !pathname.startsWith(`${localePrefix}/`)
            ) {
                pathname = `${localePrefix}${pathname}`.replace(/\/+/g, "/");
            }

            return `${pathname}${parsedUrl.search}${parsedUrl.hash}`;
        } catch (error) {
            return url;
        }
    }

    function isSearchPage() {
        return SEARCH_PAGE_PATH_REGEX.test(window.location.pathname);
    }

    function isCollectionPage() {
        return COLLECTION_PAGE_PATH_REGEX.test(window.location.pathname);
    }

    function isAllCollectionPage() {
        const match = window.location.pathname.match(COLLECTION_PAGE_PATH_REGEX);
        return Boolean(match && match[2] === "all");
    }

    function collectTopLevelCategoryNames(items) {
        if (!Array.isArray(items)) return [];

        return items.flatMap((item) => {
            const title = String(item?.title || "").trim();
            const handle = getCollectionHandleFromUrl(item?.url);

            if (shouldSkipMenuTitle(title, handle)) {
                return collectTopLevelCategoryNames(getTaxonomyChildren(item));
            }

            return title ? [title] : [];
        });
    }

    function getTopLevelCategoryFilter() {
        const names = collectTopLevelCategoryNames(window.__taxonomyMenuLinks);

        if (!names.length) return null;

        return `(${names
            .map((name) => `${CATEGORY_ATTRIBUTE}:"${name.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`)
            .join(" OR ")})`;
    }

    function getSearchQuery() {
        return new URLSearchParams(window.location.search).get("q") || "";
    }

    function hasActiveSearchQuery() {
        return Boolean(getSearchQuery().trim());
    }

    function isSearchExperience() {
        return isSearchPage() || (isCollectionPage() && hasActiveSearchQuery());
    }

    function getNativeAnalyticsTag() {
        return isSearchExperience() ? "search" : "browse";
    }

    function getDiscontinuedFilter() {
        return 'NOT meta.custom.discontinued_status:"true" AND NOT meta.custom.discontinuedstatus:true';
    }

    function getIgnoredTagFilter() {
        return 'NOT tags:"algolia-ignore"';
    }

    function formatFacetTitle(attribute) {
        return attribute
            .replace(TECH_SPEC_PREFIX, "")
            .trim();
    }

    function getSearchDynamicFacetAttributes(facets) {
        return Object.keys(facets || {})
            .filter((attribute) =>
                attribute.startsWith(TECH_SPEC_PREFIX) &&
                attribute !== TECH_SPEC_ROOT_ATTRIBUTE
            )
            .sort((a, b) => formatFacetTitle(a).localeCompare(formatFacetTitle(b)));
    }

    function getDynamicFacetConfigFromCollection() {
        return normalizeDynamicFacetConfig(window.__collectionFlattenRefineElements);
    }

    function buildDynamicFacetConfigEntry(label, priority) {
        if (!label) return null;

        const normalizedPriority = Number(priority);

        return {
            priority: Number.isFinite(normalizedPriority)
                ? normalizedPriority
                : DEFAULT_DYNAMIC_FACET_PRIORITY,
            label,
            attribute: `${TECH_SPEC_PREFIX}${label}`,
        };
    }

    function normalizeDynamicFacetConfig(flattenRefineElements) {
        const source = flattenRefineElements || {};

        if (Array.isArray(source.refineElement)) {
            return source.refineElement
                .map((item) => buildDynamicFacetConfigEntry(item?.label, item?.priority))
                .filter(Boolean)
                .sort((a, b) => a.priority - b.priority);
        }

        return Object.keys(source)
            .map((key) => {
                const match = key.match(/^(\d+)-refineElements-(.+)$/);

                if (!match) return null;

                return buildDynamicFacetConfigEntry(match[2], match[1]);
            })
            .filter(Boolean)
            .sort((a, b) => a.priority - b.priority);
    }

    function getCollectionContextByHierarchy(hierarchy) {
        const contexts = window.__taxonomyCollectionContextByHierarchy || {};
        return hierarchy ? contexts[hierarchy] || null : null;
    }

    function getTaxonomyChildren(item) {
        return item?.items || item?.links || [];
    }

    function buildHierarchyPath(parents) {
        return parents.join(HIERARCHY_SEPARATOR);
    }

    function collectHierarchyValues(items, values = []) {
        if (!Array.isArray(items)) return values;

        items.forEach((item) => {
            if (item?.value) {
                values.push(item.value);
            }

            if (Array.isArray(item?.data) && item.data.length) {
                collectHierarchyValues(item.data, values);
            }
        });

        return values;
    }

    function getHierarchyValuesFromHits(hits) {
        if (!Array.isArray(hits)) return [];

        const hierarchyValues = [];
        const seenHierarchyValues = new Set();

        hits.forEach((hit) => {
            const category = hit?.meta?.custom?.category_hierarchy || {};
            const values = [
                category.lvl3,
                category.lvl2,
                category.lvl1,
                category.lvl0,
            ]
                .filter((value) => typeof value === "string" && value.trim())
                .map((value) => value.trim());

            values.forEach((value) => {
                if (seenHierarchyValues.has(value)) return;

                seenHierarchyValues.add(value);
                hierarchyValues.push(value);
            });
        });

        return hierarchyValues;
    }

    function getSearchDynamicFacetConfig(hierarchyItems, availableFacets, hits) {
        const hierarchyValues = Array.from(
            new Set([
                ...getHierarchyValuesFromHits(hits),
                ...collectHierarchyValues(hierarchyItems || []),
            ])
        );
        const mergedConfig = new Map();

        hierarchyValues.forEach((hierarchy) => {
            const context = getCollectionContextByHierarchy(hierarchy);
            const normalizedConfig = normalizeDynamicFacetConfig(
                context?.flattenRefineElements
            );

            normalizedConfig.forEach((facet) => {
                if (!availableFacets?.[facet.attribute]) {
                    return;
                }

                const existingFacet = mergedConfig.get(facet.attribute);

                if (!existingFacet || facet.priority < existingFacet.priority) {
                    mergedConfig.set(facet.attribute, facet);
                }
            });
        });

        return Array.from(mergedConfig.values()).sort(
            (left, right) => left.priority - right.priority
        );
    }

    function escapeHtml(value) {
        return String(value)
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    }

    function getCurrentCollectionHandle() {
        if (window.__collectionContext?.handle) {
            return window.__collectionContext.handle;
        }

        const match = window.location.pathname.match(/(?:^|\/)collections\/([^\/?#]+)/);

        return match ? match[1] : "";
    }

    function getCollectionHandleFromUrl(url) {
        return String(url || "")
            .split("/collections/")[1]
            ?.split("?")[0]
            ?.replace("/", "") || "";
    }

    function shouldSkipMenuTitle(title, handle) {
        const normalizedTitle = String(title || "").trim().toLowerCase();

        return (
            handle === "all" ||
            normalizedTitle === "products" ||
            normalizedTitle === "shop all" ||
            normalizedTitle === "shop all products"
        );
    }

    function findHierarchyByHandle(items, handle, parents = []) {
        if (!Array.isArray(items) || !handle) return "";

        for (const item of items) {
            const itemHandle = getCollectionHandleFromUrl(item.url);
            const skipCurrent = shouldSkipMenuTitle(item.title, itemHandle);

            const nextParents = skipCurrent
                ? parents
                : [...parents, item.title];

            if (itemHandle === handle) {
                return buildHierarchyPath(nextParents);
            }

            const children = getTaxonomyChildren(item);
            const childMatch = findHierarchyByHandle(children, handle, nextParents);

            if (childMatch) return childMatch;
        }

        return "";
    }

    function getInitialHierarchyFromMenu() {
        return findHierarchyByHandle(
            window.__taxonomyMenuLinks,
            getCurrentCollectionHandle()
        );
    }

    function getInitialUiState(indexName) {
        const initialState = {};
        const searchQuery = getSearchQuery();
        const initialHierarchy = isSearchExperience() ? "" : getInitialHierarchyFromMenu();

        if (searchQuery) {
            initialState.query = searchQuery;
        }

        if (initialHierarchy) {
            initialState.hierarchicalMenu = {
                [CATEGORY_ATTRIBUTE]: [initialHierarchy],
            };
        }

        return Object.keys(initialState).length
            ? { [indexName]: initialState }
            : {};
    }

    function getSelectedHierarchyFromState() {
        return getNativeSearchState().search?.helper?.state?.hierarchicalFacetsRefinements?.[
            CATEGORY_ATTRIBUTE
        ]?.[0] || "";
    }

    function filterHierarchyToActivePath(items, selectedHierarchy, depth = 0) {
        const pathSegments = selectedHierarchy.split(HIERARCHY_SEPARATOR);

        if (depth >= pathSegments.length) return items;

        const activeValue = pathSegments.slice(0, depth + 1).join(HIERARCHY_SEPARATOR);

        return items
            .filter((item) => item.value === activeValue)
            .map((item) => {
                if (!Array.isArray(item.data) || !item.data.length) return item;

                return {
                    ...item,
                    data: filterHierarchyToActivePath(item.data, selectedHierarchy, depth + 1),
                };
            });
    }

    function getDynamicFacetFallbackConfig(facets) {
        return getSearchDynamicFacetAttributes(facets).map((attribute, index) => ({
            priority: index,
            label: formatFacetTitle(attribute),
            attribute,
        }));
    }

    function getConfiguredDynamicFacetConfig(searchExperience, facets, hits) {
        if (!searchExperience) {
            return [
                ...UNIVERSAL_FACET_CONFIG,
                ...getDynamicFacetConfigFromCollection(),
            ];
        }

        const searchConfig = getSearchDynamicFacetConfig(
            getNativeSearchState().hierarchyItems,
            facets,
            hits
        );

        const baseConfig = searchConfig.length
            ? searchConfig
            : getDynamicFacetFallbackConfig(facets);

        return [...UNIVERSAL_FACET_CONFIG, ...baseConfig];
    }

    function buildDynamicFacetInputId(attribute, value) {
        return `facet-${escapeHtml(attribute)}-${escapeHtml(value)}`
            .replace(/[^a-zA-Z0-9_-]/g, "-");
    }

    function buildDynamicFacetContainerId(attribute) {
        return `native-dynamic-facet-body-${buildDynamicFacetInputId(attribute, "")}`;
    }

    function buildDynamicFacetWrapperMarkup(
        attribute,
        label,
        isOpen = true,
        { searchable = false } = {}
    ) {
        return `
            <details class="native-dynamic-facet" ${isOpen ? "open" : ""}>
                <summary class="native-dynamic-facet-summary">
                    <h3>${escapeHtml(label)}</h3>
                </summary>

                ${
                    searchable
                        ? `
                            <input
                                type="search"
                                class="native-dynamic-facet-search"
                                placeholder="Search..."
                                aria-label="Search ${escapeHtml(label)}"
                            />
                        `
                        : ""
                }

                <div
                    class="native-dynamic-facet-body"
                    id="${buildDynamicFacetContainerId(attribute)}"
                ></div>
            </details>
        `;
    }

    function isDynamicFacetOpen(attribute) {
        const details = document
            .getElementById(buildDynamicFacetContainerId(attribute))
            ?.closest(".native-dynamic-facet");

        return details ? details.open : true;
    }

    function getFacetRefinementValue(value) {
        const stringValue = String(value || "");
        return stringValue.startsWith("-") ? `\\${stringValue}` : stringValue;
    }

    function buildDynamicFacetRefinementItemMarkup(attribute, item) {
        const inputId = buildDynamicFacetInputId(attribute, item.value);
        const safeValueLabel = escapeHtml(item.label);

        return `
            <label
                for="${inputId}"
                class="native-dynamic-facet-option ${item.isRefined ? "is-active" : ""}"
            >
                <input
                    id="${inputId}"
                    type="checkbox"
                    class="native-dynamic-facet-checkbox"
                    data-value="${encodeURIComponent(item.value)}"
                    ${item.isRefined ? "checked" : ""}
                />

                <span>${safeValueLabel}</span>
                ${item.count == null ? "" : `<span>${item.count}</span>`}
            </label>
        `;
    }

    function getStableRefinementItemOrder(attribute, items) {
        const itemsByValue = new Map(items.map((item) => [item.value, item]));
        const knownOrder = dynamicFacetValueOrderByAttribute.get(attribute);

        if (!knownOrder) {
            const order = items.map((item) => item.value);
            dynamicFacetValueOrderByAttribute.set(attribute, order);
            return items;
        }

        const newValues = items
            .map((item) => item.value)
            .filter((value) => !knownOrder.includes(value));

        const order = newValues.length ? knownOrder.concat(newValues) : knownOrder;
        if (newValues.length) {
            dynamicFacetValueOrderByAttribute.set(attribute, order);
        }

        return order
            .filter((value) => itemsByValue.has(value))
            .map((value) => itemsByValue.get(value));
    }

    function createDynamicFacetRefinementWidget(
        connectRefinementList,
        attribute,
        {
            operator = "or",
            limit = 50,
            searchable = false,
            refinedFirst = false,
            preserveRefinedZeroCount = false,
            preserveActiveRouteRefinements = false,
        } = {}
    ) {
        const suppressedRouteRefinements = new Set();
        return connectRefinementList(
            ({ items, refine, searchForItems }) => {
                const staleRefinedItems = preserveRefinedZeroCount
                    ? []
                    : items.filter(
                        (item) =>
                            item.isRefined &&
                            item.count === 0
                    );

                if (staleRefinedItems.length) {
                    staleRefinedItems.forEach((item) =>
                        refine(item.value)
                    );
                    return;
                }

                const visibleItems = items.filter(
                    (item) =>
                        item.count > 0 ||
                        (
                            preserveRefinedZeroCount &&
                            item.isRefined
                        )
                );

                if (preserveActiveRouteRefinements) {
                    const returnedValues = new Set(
                        visibleItems.map((item) =>
                            String(item.value)
                        )
                    );

                    getActiveYmmRouteValues()
                        .filter((value) => {
                            const stringValue = String(value);

                            return (
                                !returnedValues.has(stringValue) &&
                                !suppressedRouteRefinements.has(
                                    stringValue
                                )
                            );
                        })
                        .forEach((value) => {
                            visibleItems.unshift({
                                value,
                                label: value,
                                count: null,
                                isRefined: true,
                            });
                        });
                }

                const bodyContainer = document.getElementById(
                    buildDynamicFacetContainerId(attribute)
                );
                const wrapper = bodyContainer?.closest(
                    ".native-dynamic-facet"
                );

                if (!bodyContainer) return;

                const searchInput = wrapper?.querySelector(
                    ".native-dynamic-facet-search"
                );

                if (
                    searchable &&
                    searchInput &&
                    typeof searchForItems === "function"
                ) {
                    searchInput.oninput = (event) => {
                        searchForItems(
                            event.currentTarget.value
                        );
                    };
                }

                if (!visibleItems.length) {
                    if (!searchable) {
                        wrapper?.classList.add("is-hidden");
                    } else {
                        wrapper?.classList.remove("is-hidden");
                    }

                    bodyContainer.innerHTML = "";
                    return;
                }

                wrapper?.classList.remove("is-hidden");

                const orderedItems =
                    getStableRefinementItemOrder(
                        attribute,
                        visibleItems
                    );

                const displayItems = refinedFirst
                    ? [
                        ...orderedItems.filter(
                            (item) => item.isRefined
                        ),
                        ...orderedItems.filter(
                            (item) => !item.isRefined
                        ),
                    ]
                    : orderedItems;

                bodyContainer.innerHTML = displayItems
                    .map((item) =>
                        buildDynamicFacetRefinementItemMarkup(
                            attribute,
                            item
                        )
                    )
                    .join("");

                if (preserveActiveRouteRefinements) {
                    items
                        .filter((item) => item.isRefined)
                        .forEach((item) => {
                            suppressedRouteRefinements.delete(
                                String(item.value)
                            );
                        });
                }

                bodyContainer
                    .querySelectorAll(
                        ".native-dynamic-facet-checkbox"
                    )
                    .forEach((checkbox) => {
                        checkbox.addEventListener(
                            "change",
                            () => {
                                const value = decodeURIComponent(
                                    checkbox.dataset.value
                                );

                                if (preserveActiveRouteRefinements) {
                                    if (checkbox.checked) {
                                        suppressedRouteRefinements.delete(
                                            value
                                        );
                                    } else {
                                        suppressedRouteRefinements.add(
                                            value
                                        );
                                    }
                                }

                                refine(value);
                            }
                        );
                    });
            }
        )({
            attribute,
            operator,
            limit,
            ...(refinedFirst
                ? {
                    sortBy: [
                        "isRefined:desc",
                        "count:desc",
                        "name:asc",
                    ],
                }
                : {}),
        });
    }

    function createHiddenYmmRefinementWidget(connectRefinementList) {
        return connectRefinementList(() => {})({
            attribute: YMM_ATTRIBUTE,
            operator: "or",
            limit: 50,
        });
    }

    function getEnabledCollectionYmmFacet() {
        const collectionFacets =
            window.algoliaShopify?.config?.collection_facets?.default;

        if (!Array.isArray(collectionFacets)) return null;

        const ymmFacet = collectionFacets.find(
            (facet) =>
                facet?.name === YMM_ATTRIBUTE &&
                facet.enabled === true
        );

        if (!ymmFacet) return null;

        const configuredLimit = Number(ymmFacet.limit);

        return {
            attribute: YMM_ATTRIBUTE,
            label: ymmFacet.title || "Year Make Model",
            operator: ymmFacet.type === "conjunctive" ? "and" : "or",
            limit:
                Number.isFinite(configuredLimit) && configuredLimit > 0
                    ? configuredLimit
                    : 50,
            searchable: ymmFacet.searchable === true,
            refinedFirst: ymmFacet.refined_first === true,
            preserveRefinedZeroCount: true,
            preserveActiveRouteRefinements: true,
        };
    }

    const UNIVERSAL_FACET_CONFIG = [
        { attribute: "vendor", label: "Brand" },
    ];

    function getActiveYmmRouteValues(indexName = getIndexName()) {
        const params = new URLSearchParams(window.location.search);
        const legacyPrefix = `refinementList[${YMM_ATTRIBUTE}]`;
        const nativePrefix =
            `${indexName}[refinementList][${YMM_ATTRIBUTE}]`;

        return Array.from(params.entries())
            .filter(
                ([key]) =>
                    key.startsWith(legacyPrefix) ||
                    key.startsWith(nativePrefix)
            )
            .map(([, value]) => value)
            .filter(Boolean);
    }

    function hasActiveYmmRoute(indexName = getIndexName()) {
        return getActiveYmmRouteValues(indexName).length > 0;
    }

    function buildDynamicFacetWidgetsForCollection(connectRefinementList) {
        const ymmFacet = getEnabledCollectionYmmFacet();

        const config = [
            ...(ymmFacet ? [ymmFacet] : []),
            ...UNIVERSAL_FACET_CONFIG,
            ...getDynamicFacetConfigFromCollection().filter(
                (facet) => facet.attribute !== YMM_ATTRIBUTE
            ),
        ];
        const container = queryElement(DYNAMIC_FACETS_SELECTOR);

        if (!container || !config.length) return [];

        container.innerHTML = config
        .map((facet) =>
            buildDynamicFacetWrapperMarkup(
                facet.attribute,
                facet.label,
                true,
                {
                    searchable: facet.searchable,
                }
            )
        )
        .join("");

        const visibleWidgets = config.map((facet) =>
            createDynamicFacetRefinementWidget(
                connectRefinementList,
                facet.attribute,
                {
                    operator: facet.operator,
                    limit: facet.limit,
                    searchable: facet.searchable,
                    refinedFirst: facet.refinedFirst,
                    preserveRefinedZeroCount:
                        facet.preserveRefinedZeroCount,
                    preserveActiveRouteRefinements:
                        facet.preserveActiveRouteRefinements,
                }
            )
        );

        const hasYmmWidget = config.some(
            (facet) => facet.attribute === YMM_ATTRIBUTE
        );

        const hiddenWidgets =
            hasActiveYmmRoute() && !hasYmmWidget
                ? [createHiddenYmmRefinementWidget(connectRefinementList)]
                : [];

        return [...visibleWidgets, ...hiddenWidgets];
    }

    function findCollectionUrlByHierarchy(items, hierarchy, parents = []) {
        if (!Array.isArray(items) || !hierarchy) return "";

        for (const item of items) {
            const itemHandle = getCollectionHandleFromUrl(item.url);
            const skipCurrent = shouldSkipMenuTitle(item.title, itemHandle);
            const nextParents = skipCurrent ? parents : [...parents, String(item.title)];
            const currentHierarchy = buildHierarchyPath(nextParents);

            if (currentHierarchy === hierarchy) {
                return item.url || "";
            }

            const children = getTaxonomyChildren(item);
            const childMatch = findCollectionUrlByHierarchy(children, hierarchy, nextParents);

            if (childMatch) return childMatch;
        }

        return "";
    }

    // Mounts/unmounts real connectRefinementList widgets (the same mechanism
    // collection pages use) for whichever facet attributes are currently
    // configured for the search experience. Using real widgets — instead of a
    // hand-rolled render loop — means the official clearRefinements widget
    // ("Remove all") reliably tracks and clears them, since they're properly
    // registered with the search instance rather than mutating helper state
    // ad hoc.
    function buildSearchDynamicFacetsController(connectRefinementList) {
        const mountedWidgetsByAttribute = new Map();

        return {
            init() { },

            render({ results }) {
                const container = queryElement(DYNAMIC_FACETS_SELECTOR);
                const rawResults = results?._rawResults?.[0];
                const facets = rawResults?.facets || {};

                if (!container) return;

                const configuredFacetConfig = getConfiguredDynamicFacetConfig(
                    true,
                    facets,
                    results?.hits
                );

                const visibleFacetConfig = configuredFacetConfig.filter(
                    (facet) => facets[facet.attribute]
                );
                const nextAttributes = visibleFacetConfig.map((facet) => facet.attribute);

                setNativeSearchState({ dynamicFacetAttributes: nextAttributes });

                const mountedAttributesInOrder = Array.from(mountedWidgetsByAttribute.keys());
                const sameSet =
                    nextAttributes.length === mountedAttributesInOrder.length &&
                    nextAttributes.every(
                        (attribute, index) => attribute === mountedAttributesInOrder[index]
                    );

                if (sameSet) return;

                const search = getNativeSearchState().search;

                // Snapshot open/closed state so rebuilding markup below doesn't reopen a collapsed facet.
                const openStateByAttribute = new Map(
                    mountedAttributesInOrder.map((attribute) => [
                        attribute,
                        isDynamicFacetOpen(attribute),
                    ])
                );

                // Only add/remove the widgets whose attribute actually appeared or
                // disappeared — an attribute that's still relevant keeps its own
                // widget instance alive, so a checkbox the user is mid-click on
                // never gets torn down just because some OTHER facet's visibility
                // changed in the same render.
                const attributesToRemove = mountedAttributesInOrder.filter(
                    (attribute) => !nextAttributes.includes(attribute)
                );

                if (attributesToRemove.length) {
                    search.removeWidgets(
                        attributesToRemove.map((attribute) =>
                            mountedWidgetsByAttribute.get(attribute)
                        )
                    );
                    attributesToRemove.forEach((attribute) =>
                        mountedWidgetsByAttribute.delete(attribute)
                    );
                }

                if (!nextAttributes.length) {
                    container.innerHTML = "";
                    return;
                }

                container.innerHTML = visibleFacetConfig
                    .map((facet) =>
                        buildDynamicFacetWrapperMarkup(
                            facet.attribute,
                            facet.label,
                            openStateByAttribute.get(facet.attribute) ?? true
                        )
                    )
                    .join("");

                const newWidgets = visibleFacetConfig
                    .filter((facet) => !mountedWidgetsByAttribute.has(facet.attribute))
                    .map((facet) => {
                        const widget = createDynamicFacetRefinementWidget(
                            connectRefinementList,
                            facet.attribute
                        );
                        mountedWidgetsByAttribute.set(facet.attribute, widget);
                        return widget;
                    });

                if (newWidgets.length) {
                    search.addWidgets(newWidgets);
                }

                // Reorder the map to match nextAttributes so order-only changes don't keep failing sameSet.
                const reordered = nextAttributes.map((attribute) => [
                    attribute,
                    mountedWidgetsByAttribute.get(attribute),
                ]);
                mountedWidgetsByAttribute.clear();
                reordered.forEach(([attribute, widget]) =>
                    mountedWidgetsByAttribute.set(attribute, widget)
                );
            },
        };
    }

    function buildPaginationItemMarkup({
        label,
        pageIndex,
        itemClassName,
        disabled,
        selected,
    }) {
        const classNames = [
            "ais-Pagination-item",
            itemClassName,
            disabled ? "ais-Pagination-item--disabled" : "",
            selected ? "ais-Pagination-item--selected" : "",
        ]
            .filter(Boolean)
            .join(" ");

        if (disabled) {
            return `
                <li class="${classNames}">
                    <span class="ais-Pagination-link" aria-hidden="true">${label}</span>
                </li>
            `;
        }

        return `
            <li class="${classNames}">
                <a
                    href="#"
                    class="ais-Pagination-link"
                    data-page="${pageIndex}"
                    aria-label="${escapeHtml(getPaginationAriaLabel(pageIndex + 1))}"
                    ${selected ? 'aria-current="page"' : ""}
                >${label}</a>
            </li>
        `;
    }

    function buildAddToCartButtonId(objectId) {
        return `btn-add-to-cart-${String(objectId || "").replace(
            /[^a-zA-Z0-9_-]/g,
            "-"
        )}`;
    }

    function nativePaginationRenderer() {
        return {
            init() { },

            render({ results, helper }) {
                const container = queryElement(PAGINATION_SELECTOR);

                if (!container || !results) return;

                const hitsPerPage = Number(
                    helper?.state?.hitsPerPage || results.hitsPerPage || 0
                );
                const computedPages = hitsPerPage
                    ? Math.ceil(Number(results.nbHits || 0) / hitsPerPage)
                    : 0;
                const totalPages = Math.max(
                    Number(results.nbPages || 0),
                    computedPages
                );
                const currentPage = Number(results.page || 0);

                if (totalPages <= 1) {
                    container.innerHTML = "";
                    return;
                }

                const visiblePages = Math.min(totalPages, MAX_VISIBLE_PAGINATION_PAGES);
                const halfWindow = Math.floor(visiblePages / 2);
                let startPage = Math.max(0, currentPage - halfWindow);
                let endPage = startPage + visiblePages - 1;

                if (endPage >= totalPages) {
                    endPage = totalPages - 1;
                    startPage = Math.max(0, endPage - visiblePages + 1);
                }

                const pages = Array.from(
                    { length: endPage - startPage + 1 },
                    (_, index) => startPage + index
                );

                const pageItems = pages
                    .map((pageIndex) =>
                        buildPaginationItemMarkup({
                            label: String(pageIndex + 1),
                            pageIndex,
                            itemClassName: "ais-Pagination-item--page",
                            disabled: false,
                            selected: pageIndex === currentPage,
                        })
                    )
                    .join("");

                container.innerHTML = `
                    <div class="ais-Pagination native-pagination">
                        <ul class="ais-Pagination-list">
                            ${buildPaginationItemMarkup({
                    label: "\u2039",
                    pageIndex: currentPage - 1,
                    itemClassName: "ais-Pagination-item--previousPage",
                    disabled: currentPage <= 0,
                    selected: false,
                })}
                            ${pageItems}
                            ${buildPaginationItemMarkup({
                    label: "\u203A",
                    pageIndex: currentPage + 1,
                    itemClassName: "ais-Pagination-item--nextPage",
                    disabled: currentPage >= totalPages - 1,
                    selected: false,
                })}
                        </ul>
                    </div>
                `;

                container
                    .querySelectorAll(".ais-Pagination-link[data-page]")
                    .forEach((link) => {
                        link.addEventListener("click", (event) => {
                            event.preventDefault();

                            const pageIndex = Number(link.dataset.page);

                            if (!Number.isFinite(pageIndex) || pageIndex === currentPage) {
                                return;
                            }

                            helper.setPage(pageIndex);
                            helper.search();
                        });
                    });
            },
        };
    }

    function isProductDeactivated(hit) {
        const deactiveDate = hit?.meta?.custom?.deactiveDate;
        return Boolean(deactiveDate) && new Date(deactiveDate) < new Date();
    }

    function hitTemplate(hit) {
        const image = hit.product_image || hit.image || window.fallbackImage;
        const title = hit.title || "";
        const itemNumber = hit.meta?.custom?.externalId || hit.sku || "";
        const url = hit.handle
            ? localizeStorefrontUrl(`/products/${hit.handle}`)
            : "#";
        const safeTitle = escapeHtml(title);
        const safeImage = escapeHtml(image);
        const safeUrl = escapeHtml(url);
        const addToCartButtonId = buildAddToCartButtonId(hit.objectID);
        const deactivated = isProductDeactivated(hit);
        const missingIds = hit.id == null || !hit.objectID;
        const addToCartLabel = deactivated
            ? window.translations?.product?.discontinued || "Discontinued"
            : window.translations?.cart?.add_to_cart || "Add to Cart";

        return `
                <div class="product-item ${hit.product_image ? "" : "product--no-image"}">
                    <div class="product-item__img">
                        <a href="${safeUrl}">
                            <img
                                src="${safeImage}"
                                alt="${safeTitle}"
                                loading="lazy"
                                data-product-title="${safeTitle}"
                            />
                        </a>
                    </div>

                    <div class="product-item__text">
                        <h2>
                            <a href="${safeUrl}" data-product-title="${safeTitle}">
                                ${safeTitle}
                            </a>
                        </h2>

                        ${itemNumber ? `<h4>${escapeHtml(getItemNumberLabel())}#: ${escapeHtml(itemNumber)}</h4>` : ""}
                    </div>

                    <div class="skeleton-wrapper hidden">
                        <div class="swipe"></div>
                    </div>
                    <p
                        class="price-point hidden"
                        data-collection-product-sku="${escapeHtml(hit.sku || "")}"
                    ></p>
                </div>


                <p class="ais-hit--cart">
                    <button
                        class="ais-hit--cart-button btn btn--small button button--full-width button button--primary ${deactivated || missingIds ? "" : "bg--yellow"}"
                        id="${addToCartButtonId}"
                        data-product-id="${escapeHtml(hit.id ?? "")}"
                        data-variant-id="${escapeHtml(hit.objectID ?? "")}"
                        data-product-title="${safeTitle}"
                        data-external-id="${escapeHtml(itemNumber)}"
                        ${deactivated || missingIds ? "disabled" : ""}
                    >
                        ${escapeHtml(addToCartLabel)}
                    </button>
                </p>
            `;
    }

    function createSearchClient() {
        const { app_id, search_api_key } = getAlgoliaConfig();
        const ns = window["algoliasearch/lite"];
        const create = (ns && ns.liteClient) || window.algoliasearch;
        return create(app_id, search_api_key);
    }

    function createCollectionStateMapping(indexName) {
        const simpleMapping = window.instantsearch.stateMappings.simple();

        return {
            stateToRoute(uiState) {
                const route = simpleMapping.stateToRoute(uiState);
                const indexRoute = route?.[indexName];

                if (!indexRoute || !("hierarchicalMenu" in indexRoute)) {
                    return route;
                }

                const { hierarchicalMenu, ...restOfRoute } = indexRoute;

                return { ...route, [indexName]: restOfRoute };
            },
            routeToState(routeState) {
                const legacyRefinementList = routeState?.refinementList || {};
                const {
                    [YMM_ATTRIBUTE]: legacyYmmRefinements,
                    ...remainingLegacyRefinements
                } = legacyRefinementList;

                const normalizedRouteState = {
                    ...(routeState || {}),
                };

                if (legacyYmmRefinements) {
                    if (Object.keys(remainingLegacyRefinements).length) {
                        normalizedRouteState.refinementList =
                            remainingLegacyRefinements;
                    } else {
                        delete normalizedRouteState.refinementList;
                    }
                }

                const state = simpleMapping.routeToState(normalizedRouteState);

                if (!legacyYmmRefinements) {
                    return state;
                }

                const ymmValues = Array.isArray(legacyYmmRefinements)
                    ? legacyYmmRefinements
                    : [legacyYmmRefinements];

                const indexState = state[indexName] || {};

                state[indexName] = {
                    ...indexState,
                    refinementList: {
                        ...(indexState.refinementList || {}),
                        [YMM_ATTRIBUTE]: ymmValues,
                    },
                };

                return state;
            },
        };
    }

    // Keeps the URL's search term on the plain "q" param (the format the rest
    // of the project — and prod — already relies on) instead of letting the
    // router rewrite it into its own "<indexName>[query]=" format.
    function createSearchStateMapping(indexName) {
        const simpleMapping = window.instantsearch.stateMappings.simple();

        return {
            stateToRoute(uiState) {
                const route = simpleMapping.stateToRoute(uiState);
                const indexRoute = route?.[indexName];

                if (!indexRoute) {
                    return route;
                }

                const { query, ...restOfRoute } = indexRoute;
                const hasQuery = Object.prototype.hasOwnProperty.call(indexRoute, 'query');
                const nextQuery = hasQuery ? query : getSearchQuery();

                return {
                    ...route,
                    [indexName]: restOfRoute,
                    ...(hasQuery || nextQuery ? { q: nextQuery } : {}),
                };
            },
            routeToState(routeState) {
                const { q, ...restOfRouteState } = routeState || {};
                const state = simpleMapping.routeToState(restOfRouteState);

                if (q?.trim()) {
                    state[indexName] = { ...(state[indexName] || {}), query: q };
                }

                return state;
            },
        };
    }

    function createSearchInstance(searchClient, indexName, searchExperience) {
        return window.instantsearch({
            indexName,
            searchClient,
            routing: {
                router: window.instantsearch.routers.history({ writeDelay: 0 }),
                stateMapping: searchExperience
                    ? createSearchStateMapping(indexName)
                    : createCollectionStateMapping(indexName),
            },
            initialUiState: getInitialUiState(indexName),
        });
    }

    function getInstantSearchWidgets() {
        return window.instantsearch.widgets;
    }

    function getInstantSearchConnectors() {
        return window.instantsearch.connectors;
    }

    function getRuleContexts() {
        if (!isCollectionPage() || isSearchExperience()) return [];

        const handle = getCurrentCollectionHandle();

        if (!handle || handle === "all") return [];

        return [handle, "shopify_default_collection"];
    }

    function buildConfigureWidget(configure, searchQuery) {
        const filterParts = [
            getDiscontinuedFilter(),
            getIgnoredTagFilter(),
        ];

        if (isAllCollectionPage()) {
            const topLevelCategoryFilter = getTopLevelCategoryFilter();
            if (topLevelCategoryFilter) filterParts.push(topLevelCategoryFilter);
        } else if (isCollectionPage() && window.__collectionContext?.id && !getInitialHierarchyFromMenu()) {
            filterParts.push(`(collection_ids:"${window.__collectionContext.id}")`);
        }

        const ruleContexts = getRuleContexts();

        return configure({
            hitsPerPage: HITS_PER_PAGE,
            filters: filterParts.join(" AND "),
            facets: ["*"],
            analytics: true,
            clickAnalytics: true,
            analyticsTags: [getNativeAnalyticsTag()],
            ...(ruleContexts.length ? { ruleContexts } : {}),
            ...(searchQuery ? { query: searchQuery } : {}),
        });
    }

    function getHierarchyVisibilityState(searchExperience) {
        const { dynamicFacetAttributes } = getNativeSearchState();
        const hasDynamicFacetConfig = searchExperience
            ? dynamicFacetAttributes.length > 0
            : UNIVERSAL_FACET_CONFIG.length > 0 ||
            getDynamicFacetConfigFromCollection().length > 0;
        // A "leaf" collection is one with its OWN configured facets (e.g. Style,
        // Type) — not just the always-present universal Brand facet. Using
        // hasDynamicFacetConfig here would mark every collection as a leaf and
        // hide the category hierarchy tree everywhere.
        const hasSelectedLeaf = searchExperience
            ? false
            : getDynamicFacetConfigFromCollection().length > 0;

        return { hasDynamicFacetConfig, hasSelectedLeaf };
    }

    function buildHierarchicalMenuWidget(hierarchicalMenu, searchExperience) {
        return hierarchicalMenu({
            container: HIERARCHICAL_MENU_SELECTOR,
            attributes: HIERARCHICAL_ATTRIBUTES,
            separator: HIERARCHY_SEPARATOR,
            showParentLevel: true,
            limit: HIERARCHICAL_MENU_LIMIT,

            transformItems(items) {
                const selectedHierarchy = getSelectedHierarchyFromState();

                if (searchExperience) {
                    setNativeSearchState({ hierarchyItems: items });
                }

                const { hasDynamicFacetConfig, hasSelectedLeaf } =
                    getHierarchyVisibilityState(searchExperience);

                toggleLeafCollectionState(hasSelectedLeaf);
                toggleSecondaryFacetsVisibility(hasDynamicFacetConfig);

                if (searchExperience) {
                    return items;
                }

                if (selectedHierarchy) {
                    return filterHierarchyToActivePath(items, selectedHierarchy);
                }

                return items;
            },

            templates: {
                item(item, { html }) {
                    const hierarchy = item.value || item.label || "";
                    const collectionUrl = localizeStorefrontUrl(
                        findCollectionUrlByHierarchy(
                            window.__taxonomyMenuLinks,
                            hierarchy
                        )
                    );
                    const itemUrl = localizeStorefrontUrl(item.url || "#");
                    const shouldRedirect =
                        !searchExperience &&
                        Boolean(collectionUrl);

                    return html`
                    <a
                        href="${itemUrl}"
                        class="native-taxonomy-link ${item.isRefined ? "is-active" : ""}"
                        aria-current="${item.isRefined ? "true" : "false"}"
                        data-should-redirect="${shouldRedirect ? "true" : "false"}"
                        data-collection-url="${collectionUrl || ""}"
                    >
                        <span class="native-taxonomy-label">${item.label}</span>
                        <span class="native-taxonomy-count">
                            ${item.count.toLocaleString()}
                        </span>
                    </a>
                    `;
                },
            },
        });
    }

    function buildStatsWidget(stats) {
        return stats({
            container: STATS_SELECTOR,
            templates: {
                text(data) {
                    return getResultsFoundLabel(Number(data.nbHits || 0));
                },
            },
        });
    }

    // Kept separate so buildStatsWidget's render template stays a pure function of its data.
    function buildTitleSyncWidget(connectStats) {
        return connectStats(({ nbHits, query }) => {
            const nbHitsValue = Number(nbHits || 0);
            const renderedQuery = String(query || "").trim().toLowerCase();
            const urlQuery = getSearchQuery().trim().toLowerCase();

            if (renderedQuery === urlQuery) {
                updateBrowserTitle(nbHitsValue);
            }
        })({});
    }

    function buildHitsWidget(hits) {
        return hits({
            container: HITS_SELECTOR,
            templates: {
                empty() {
                    return `
                        <div class="algolia-no-results">
                            <h2>${window.translations.algolia.no_results_title}</h2>
                            <p>${window.translations.algolia.no_results_description}</p>
                            <div class="no-results-actions">
                                <a href="${localizeStorefrontUrl("/")}" class="btn btn--primary">
                                    ${window.translations.algolia.no_results_home}
                                </a>
                                <a href="${localizeStorefrontUrl("/collections/all")}" class="link">
                                    ${window.translations.algolia.no_results_shop_all}
                                </a>
                            </div>
                        </div>
                    `;
                },
                item(hit) {
                    return hitTemplate(hit);
                },
            },
        });
    }

    function buildSortByWidget(sortBy, indexName) {
        return sortBy({
            container: SORT_WIDGET_SELECTOR,
            items: [
                { label: getSortByRelevanceLabel(), value: indexName },
                {
                    label: getSortByMostPopularLabel(),
                    value: getMostPopularIndexName(indexName),
                },
            ],
        });
    }

    function buildClearRefinementsWidget(clearRefinements) {
        return clearRefinements({
            container: CLEAR_REFINEMENTS_SELECTOR,
            excludedAttributes: [
                ...HIERARCHICAL_ATTRIBUTES,
                "query",
            ],
            templates: {
                resetLabel() {
                    return getClearRefinementsLabel();
                },
            },
        });
    }

    function buildWidgets(searchExperience, searchQuery, indexName) {
        const {
            configure,
            hierarchicalMenu,
            hits,
            stats,
            clearRefinements,
            sortBy,
        } = getInstantSearchWidgets();
        const { connectRefinementList, connectStats } = getInstantSearchConnectors();
        const dynamicFacetAttributes = [
            ...UNIVERSAL_FACET_CONFIG,
            ...getDynamicFacetConfigFromCollection(),
        ].map((facet) => facet.attribute);
        const widgets = [
            buildConfigureWidget(configure, searchQuery),
            buildHierarchicalMenuWidget(hierarchicalMenu, searchExperience),
            ...(searchExperience
                ? [buildSearchDynamicFacetsController(connectRefinementList)]
                : buildDynamicFacetWidgetsForCollection(connectRefinementList)),
            buildStatsWidget(stats),
            buildSortByWidget(sortBy, indexName),
            buildHitsWidget(hits),
            nativePaginationRenderer(),
        ];

        if (searchExperience) {
            widgets.push(buildTitleSyncWidget(connectStats));
        }

        if (searchExperience || dynamicFacetAttributes.length > 0) {
            widgets.push(
                buildClearRefinementsWidget(clearRefinements)
            );
        }

        return widgets;
    }

    function setNativeResultState(hasResults) {
        const body = document.body;

        body.classList.remove("algolia-search-loading");

        body.classList.toggle("algolia-have-results-active", hasResults);
        body.classList.toggle("algolia-no-results-active", !hasResults);
    }

    let hasFiredInitialSearchAnalytics = false;
    let pricePointRefreshTimer = null;

    function scheduleNativePricePointRefresh() {
        if (!window.fetchAndDisplayPricePoint) return;

        clearTimeout(pricePointRefreshTimer);
        pricePointRefreshTimer = setTimeout(() => {
            window.fetchAndDisplayPricePoint(document);
        }, 250);
    }

    function bindProductClickAnalytics() {
        queryElement(HITS_SELECTOR)?.addEventListener("click", (event) => {
            const link = event.target.closest(
                ".product-item__img a, .product-item__text a"
            );

            if (!link) return;

            const productTitle = link.getAttribute("data-product-title");

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "product_click",
                product: productTitle,
                profile_id: window.__accountId || "",
                account_id: window.__accountId || "anonymous",
                source_detected: window.source || "web",
            });
        });
    }

    function fireNativeSearchAnalytics() {
        const results = getNativeSearchState().search?.helper?.lastResults;

        if (!results) {
            return;
        }

        const productItems = document.querySelectorAll("#native-hits .product-item");
        const hasResults = document.querySelectorAll("#native-hits .ais-hit--cart").length > 0;

        setNativeResultState(hasResults);

        scheduleNativePricePointRefresh();

        if (hasFiredInitialSearchAnalytics) return;
        hasFiredInitialSearchAnalytics = true;

        if (typeof window.searchGTM === "function") {
            window.searchGTM(window.algoliaLastQuery || "", productItems.length);
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "view_item_list",
            product_list_title: "Search Results",
            items: Array.from(productItems).map((item) => ({
                item_name: item.querySelector("a")?.dataset.productTitle || null,
                item_id: item.querySelector("button")?.id || null,
            })),
            source_detected: "algolia_search",
        });

        if (!hasResults) {
            window.dataLayer.push({
                event: "no_search_results",
                search_term: window.algoliaLastQuery || "",
                profile_id: window.__accountId || "",
                account_id: window.__accountId || "anonymous",
                source_detected: window.source || "web",
            });
        }
    }

    function handleSearchRender(searchExperience) {
        const clearButton = queryElement(CLEAR_REFINEMENTS_BUTTON_SELECTOR);

        if (searchExperience) {
            setInitializingState(false);
        }

        queryElement(SECONDARY_FACETS_HEADER_SELECTOR)?.classList.toggle(
            "is-hidden",
            !clearButton || clearButton.disabled
        );

        setInitializingState(false);
        fireNativeSearchAnalytics();
    }

    function bindHierarchyRedirects(searchExperience) {
        queryElement(HIERARCHICAL_MENU_SELECTOR)
            ?.addEventListener("click", (event) => {
                const link = event.target.closest(".native-taxonomy-link");

                if (!link) return;

                const shouldRedirect = link.dataset.shouldRedirect === "true";
                const collectionUrl = link.dataset.collectionUrl || link.getAttribute("href");

                if (searchExperience) {
                    return;
                }

                if (!shouldRedirect) {
                    return;
                }

                if (!collectionUrl || collectionUrl === "#") return;

                event.preventDefault();
                event.stopPropagation();

                window.location.href = collectionUrl;
            }, true);
    }

    function bindAddToCartButtons() {
        queryElement(HITS_SELECTOR)
            ?.addEventListener("click", (event) => {
                const button = event.target.closest(".ais-hit--cart-button");

                if (!button || button.disabled) return;

                event.preventDefault();

                const productId = button.dataset.productId;
                const variantId = button.dataset.variantId;
                const title = button.dataset.productTitle;
                const externalId = button.dataset.externalId;

                if (
                    !productId ||
                    !variantId ||
                    typeof window.aaProductAddToCart !== "function"
                ) {
                    return;
                }

                window.aaProductAddToCart(
                    productId,
                    variantId,
                    title,
                    externalId,
                    1,
                    button
                );
            });
    }

    function bindMobileFiltersToggle() {
        const toggle = document.querySelector(FACETS_TOGGLE_SELECTOR);
        const container = queryElement(NATIVE_AIS_SELECTOR);

        if (!toggle || !container) return;

        const label = toggle.querySelector(".native-facets-toggle-label");

        toggle.addEventListener("click", () => {
            const isOpen = container.classList.toggle("native-filters-open");
            toggle.setAttribute("aria-expanded", String(isOpen));
            if (label) {
                label.textContent = isOpen
                    ? window.translations.algolia.hide_filters
                    : window.translations.algolia.show_filters;
            }
        });
    }

    function bindClearRefinementsFacetSync() {
        document.addEventListener("click", (event) => {
            if (!event.target.closest(CLEAR_REFINEMENTS_BUTTON_SELECTOR)) return;

            queryElement(DYNAMIC_FACETS_SELECTOR)
                ?.querySelectorAll(".native-dynamic-facet-checkbox")
                .forEach((checkbox) => {
                    checkbox.checked = false;
                    checkbox
                        .closest(".native-dynamic-facet-option")
                        ?.classList.remove("is-active");
                });
        });
    }

    function hasActiveRoutedRefinements(searchExperience) {
        const helper = getNativeSearchState().search?.helper;

        if (!helper) return false;

        const hasDisjunctive = Object.values(
            helper.state.disjunctiveFacetsRefinements || {}
        ).some((values) => (values || []).length > 0);

        if (!searchExperience) {
            return hasDisjunctive;
        }

        const hasHierarchical = Object.values(
            helper.state.hierarchicalFacetsRefinements || {}
        ).some((values) => (values || []).length > 0);

        return hasDisjunctive || hasHierarchical;
    }

    function guardRoutedUrl(searchExperience) {
        let lastGoodSearch = window.location.search;
        let suppressUntil = 0;

        document.addEventListener(
            "click",
            (event) => {
                if (event.target.closest(CLEAR_REFINEMENTS_BUTTON_SELECTOR)) {
                    suppressUntil = Date.now() + 1000;
                }
            },
            true
        );

        const patchHistoryMethod = (methodName) => {
            const original = window.history[methodName].bind(window.history);

            window.history[methodName] = function guardedHistoryMethod(
                state,
                title,
                url
            ) {
                if (
                    Date.now() >= suppressUntil &&
                    lastGoodSearch &&
                    url &&
                    hasActiveRoutedRefinements(searchExperience)
                ) {
                    const nextUrl = new URL(url, window.location.href);

                    if (
                        !nextUrl.search &&
                        nextUrl.pathname === window.location.pathname
                    ) {
                        nextUrl.search = lastGoodSearch;
                        url = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
                    }
                }

                return original(state, title, url);
            };
        };

        patchHistoryMethod("pushState");
        patchHistoryMethod("replaceState");

        setInterval(() => {
            if (Date.now() < suppressUntil) return;

            if (window.location.search) {
                lastGoodSearch = window.location.search;
            }
        }, 400);
    }

    if (!document.querySelector(NATIVE_AIS_SELECTOR)) {
        return;
    }

    waitForNativeAlgolia()
        .then(() => {
            resetNativeSearchState();
            const searchClient = createSearchClient();
            const indexName = getIndexName();
            const searchQuery = getSearchQuery();
            const searchExperience = isSearchExperience();
            const search = createSearchInstance(searchClient, indexName, searchExperience);

            setNativeSearchState({
                search,
                searchClient,
                indexName,
            });
            window.__nativeSearch = search;
            window.__nativeSearchClient = searchClient;
            window.__nativeSearchIndexName = indexName;
            setInitializingState(true);
            const widgets = buildWidgets(searchExperience, searchQuery, indexName);

            search.addWidgets(widgets);

            search.on("render", () => {
                handleSearchRender(searchExperience);
            });

            search.on("error", ({ error }) => {
                setInitializingState(false);
                document.body.classList.remove("algolia-search-loading");
                console.error(error);
            });

            bindHierarchyRedirects(searchExperience);
            bindAddToCartButtons();
            bindProductClickAnalytics();
            bindMobileFiltersToggle();
            bindClearRefinementsFacetSync();
            guardRoutedUrl(searchExperience);
            search.start();
        })
        .catch((error) => {
            setInitializingState(false);
            document.body.classList.remove("algolia-search-loading");
            console.error(error);
        });
})();
